import { useCallback } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form'
import { Input } from 'components/ui/input'
import { useAppDispatch } from 'store/store'
import { saveUpdateTask } from 'store/updateTasks/updateTaskSlice'

const formSchema = z.object({
  file: z
    .instanceof(FileList)
    .refine((files) => files.length > 0, {
      message: 'Please upload a file.',
    })
    .refine(
      (files) => files?.[0]?.type === 'application/json',
      'Please upload a JSON file.'
    ),
})

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TasksImportDialog({ open, onOpenChange }: Props) {
  const dispatch = useAppDispatch()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { file: undefined },
  })

  const fileRef = form.register('file')

  const onSubmit = useCallback(
    async (runAfterSave: boolean) => {
      const files = form.getValues('file')
      const file = files?.[0]

      if (file) {
        const reader = new FileReader()
        reader.onload = () => {
          dispatch(
            saveUpdateTask({
              file: reader.result as string, // Data URL (readAsDataURL)
              filename: file.name,
              run_after_save: runAfterSave,
            })
          )
          onOpenChange(false)
          form.reset()
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        }
        reader.onerror = (error) => {
          console.error('Error reading file:', error)
          // Handle the error appropriately
        }
        reader.readAsDataURL(file)
      } else {
        onOpenChange(false)
        form.reset()
      }
    },
    [dispatch, form, onOpenChange]
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val)
        form.reset()
      }}
    >
      <DialogContent className='gap-2 sm:max-w-sm'>
        <DialogHeader className='text-left'>
          <DialogTitle>Import Tasks</DialogTitle>
          <DialogDescription>
            Import tasks quickly from a file.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form id='task-import-form'>
            <FormField
              control={form.control}
              name='file'
              render={() => (
                <FormItem className='mb-2 space-y-1'>
                  <FormLabel>File</FormLabel>
                  <FormControl>
                    <Input
                      type='file'
                      accept='text/csv'
                      {...fileRef}
                      className='h-8 py-0'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter className='gap-2 sm:gap-0'>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
          <Button
            type='button'
            onClick={() => form.handleSubmit(() => onSubmit(false))()}
          >
            Carica
          </Button>
          <Button
            type='button'
            onClick={() => form.handleSubmit(() => onSubmit(true))()}
          >
            Carica ed esegui
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
